/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * N-10 (S629) — ONE reader of "what locale should this number or date be
 * formatted in?".
 *
 * Before this file the shop had four answers to that question, in four places:
 * `Intl.NumberFormat("en-US", …)` in the currency service, the same in the
 * currency context, `toLocaleDateString("en-GB", …)` in lib/utils.ts, and
 * roughly fifteen bare `toLocaleString()` calls across the cart and dashboard
 * which follow the BROWSER's language rather than the shop's. So a German
 * customer would read translated words around United States prices and British
 * dates, and one page could mix two conventions depending on which helper drew
 * which figure.
 *
 * The answer comes from the shop's own address, which is the source of truth for
 * the language everywhere else too. It is deliberately NOT the browser's
 * language: a customer who chose German on a French machine must read German
 * prices, and the whole point of the language switcher is that the customer's
 * choice wins.
 *
 * Portuguese resolves to `pt-BR`, matching the decision that the shop speaks
 * Brazilian Portuguese. The grouping and the date order genuinely differ between
 * the two varieties, so a bare `pt` would be a third answer to a settled
 * question.
 */

import { dateLocaleFor, localeFor } from "@/lib/supported-languages";
import { langFromPath } from "@/lib/localized-path";
import { preferredLanguage } from "@/lib/lang-storage";

/**
 * The BCP-47 locale tag for the language the shop is currently showing.
 *
 * Address first, stored preference second, English last — the same order the
 * translation engine uses, so the words and the numbers on a page can never
 * come from two different languages.
 */
export function activeLanguage(): string {
  try {
    const fromPath = langFromPath(window.location.pathname);
    if (fromPath) return fromPath;
  } catch {
    /* no window (tests, tooling) — fall through */
  }
  return preferredLanguage();
}

/** The locale tag for numbers and money in the shop's active language. */
export function activeLocale(): string {
  return localeFor(activeLanguage());
}

/**
 * The locale tag for DATES in the shop's active language.
 *
 * Separate from activeLocale on purpose: English keeps the "27 Aug 2026" form
 * this shop has always used, which is also what the Billing & Payments column
 * widths are measured against. See LANGUAGE_DATE_LOCALES.
 */
export function activeDateLocale(): string {
  return dateLocaleFor(activeLanguage());
}

/** A number formatted in the shop's active language. */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  try {
    return new Intl.NumberFormat(activeLocale(), options).format(value);
  } catch {
    return String(value);
  }
}

/** A date formatted in the shop's active language. */
export function formatDateInActiveLocale(
  date: Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  try {
    return date.toLocaleDateString(activeDateLocale(), options);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/**
 * A date AND a clock time formatted in the shop's active language.
 *
 * Separate from formatDateInActiveLocale because `toLocaleDateString` drops
 * every time field it is handed. The order timestamp on the Thank You page is
 * the one figure in this shop that is a moment rather than a calendar day, so
 * it needs the time and its zone beside the date. It is here, and not written
 * out at the call site, so that the shop still has ONE reader of the question
 * "what locale should this be in" -- which is the whole of N-10.
 */
export function formatDateTimeInActiveLocale(
  date: Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  try {
    return date.toLocaleString(activeDateLocale(), options);
  } catch {
    return date.toISOString();
  }
}
